import React from "react";
import { FileText, AlertTriangle } from "lucide-react";
import { POConfirmationStatus } from "src/types";
import { CollapsibleSection } from "src/components/ui/collapsible";
import { FileDownloadButton } from "src/components";
import { PurchaseOrderService } from "src/services";
import { useFileDownload } from "src/hooks/useFileDownload";

interface PurchaseOrderCommercialInvoiceSectionProps {
  purchaseOrderId: string;
  confirmationStatus?: POConfirmationStatus;
  purchaseOrderService: PurchaseOrderService;
}

const PurchaseOrderCommercialInvoiceSection: React.FC<PurchaseOrderCommercialInvoiceSectionProps> = ({
  purchaseOrderId,
  confirmationStatus,
  purchaseOrderService
}) => {
  const { mutate: downloadPdf, isPending: isPdfPending, error: pdfError, retry: retryPdf, reset: resetPdf } =
    useFileDownload<File>();
  const { mutate: downloadCsv, isPending: isCsvPending, error: csvError, retry: retryCsv, reset: resetCsv } =
    useFileDownload<File>();

  const isAvailable = confirmationStatus === "Confirmed";

  const error = pdfError ?? csvError;
  const retry = pdfError ? retryPdf : retryCsv;

  const handleDownloadPdf = () => {
    resetPdf();
    downloadPdf({
      getFileData: () => purchaseOrderService.getPurchaseOrderCommercialInvoice(purchaseOrderId, "PDF")
    });
  };

  const handleDownloadCsv = () => {
    resetCsv();
    downloadCsv({
      getFileData: () => purchaseOrderService.getPurchaseOrderCommercialInvoice(purchaseOrderId, "CSV")
    });
  };

  return (
    <div className="bg-white border border-secondary-200 rounded-xl shadow-soft overflow-hidden">
      <CollapsibleSection
        initiallyOpen={false}
        title={
          <h3 className="text-lg font-semibold text-secondary-800 flex items-center">
            <FileText className="w-5 h-5 mr-2 text-primary-600" />
            Commercial Invoice
          </h3>
        }
      >
        <div className="p-6 space-y-4">
          <div className="border border-secondary-200 rounded-lg p-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div>
              <p className="text-xs font-semibold uppercase text-secondary-600 mb-1">Commercial Invoice</p>
              <p className="text-sm text-secondary-700">
                {isAvailable
                  ? "Generated from the latest purchase order data, including Shopify commodity codes and country of origin."
                  : "Available once the purchase order has been confirmed by the brand."}
              </p>
            </div>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <FileDownloadButton
                handleDownload={handleDownloadPdf}
                isDisabled={!isAvailable || isPdfPending}
                buttonText={isPdfPending ? "Preparing..." : "Download PDF"}
              />
              <FileDownloadButton
                handleDownload={handleDownloadCsv}
                isDisabled={!isAvailable || isCsvPending}
                buttonText={isCsvPending ? "Preparing..." : "Download CSV"}
              />
            </div>
          </div>

          {error && (
            <div className="flex items-center gap-3 bg-error-50 border border-error-200 rounded-lg px-4 py-3 text-sm text-error-800">
              <AlertTriangle className="w-4 h-4 flex-shrink-0" />
              <span>{error.message}</span>
              <button type="button" className="underline font-semibold ml-auto" onClick={retry}>
                Retry
              </button>
            </div>
          )}
        </div>
      </CollapsibleSection>
    </div>
  );
};

export { PurchaseOrderCommercialInvoiceSection };
